import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { exploreAPI, locationAPI } from '../../services/api';
import { User, ExploreFilters, ExploreResponse, FilterOptions, FilterHistory, FilterPreset } from '../../types';
import toast from 'react-hot-toast';

interface ExploreState {
  users: User[];
  loading: boolean;
  error: string | null;
  filters: ExploreFilters;
  filterOptions: FilterOptions | null;
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
  hasMore: boolean;
  filterPresets: FilterPreset[];
  filterHistory: FilterHistory[];
}

const initialState: ExploreState = {
  users: [],
  loading: false,
  error: null,
  filters: {
    page: 1,
    limit: 20,
    sort_by: 'smart_sort',
    sort_order: 'desc',
  },
  filterOptions: null,
  pagination: {
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  }, // Make sure this is always initialized
  hasMore: true,
  filterPresets: [],
  filterHistory: [],
};

export const fetchExploreUsers = createAsyncThunk(
  'explore/fetchExploreUsers',
  async (params: { filters?: ExploreFilters; loadMore?: boolean } = {}, { rejectWithValue, dispatch }) => {
    try {
      const response = await exploreAPI.getExploreUsers(params.filters);
      
      // Auto-save to history if filters are applied
      const hasFilters = params.filters && (
        params.filters.user_type || params.filters.location || params.filters.specializations ||
        params.filters.experience || params.filters.rating || params.filters.search
      );
      
      if (hasFilters && response.data.data.pagination.total > 0) {
        // Save to history asynchronously
        exploreAPI.saveFilterHistory({
          filters: params.filters!,
          result_count: response.data.data.pagination.total
        }).catch(error => {
          console.warn('Failed to save filter history:', error);
        });
      }
     
      return { data: response.data.data, loadMore: params.loadMore || false };
    } catch (error: any) {
      // console.log(error)
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch users');
    }
  }
);

// Add these new thunks
export const saveFilterPreset = createAsyncThunk(
  'explore/saveFilterPreset',
  async (data: { name: string; filters: ExploreFilters }, { rejectWithValue }) => {
    try {
      const response = await exploreAPI.saveFilterPreset(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to save preset');
    }
  }
);

export const fetchFilterPresets = createAsyncThunk(
  'explore/fetchFilterPresets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await exploreAPI.getFilterPresets();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch presets');
    }
  }
);

export const deleteFilterPreset = createAsyncThunk(
  'explore/deleteFilterPreset',
  async (presetId: string, { rejectWithValue }) => {
    try {
      await exploreAPI.deleteFilterPreset(presetId);
      return presetId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete preset');
    }
  }
);

export const fetchFilterHistory = createAsyncThunk(
  'explore/fetchFilterHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await exploreAPI.getFilterHistory();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch history');
    }
  }
);

// export const fetchExploreUsers = createAsyncThunk(
//   'explore/fetchExploreUsers',
//   async (params: { filters?: ExploreFilters; loadMore?: boolean } = {}, { rejectWithValue }) => {
//     try {
//       const response = await exploreAPI.getExploreUsers(params.filters);
//       // console.log("response", response)
//       return { data: response.data.data, loadMore: params.loadMore || false };
//     } catch (error: any) {
//       return rejectWithValue(error.response?.data?.error || 'Failed to fetch users');
//     }
//   }
// );

export const fetchFilterOptions = createAsyncThunk(
  'explore/fetchFilterOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await exploreAPI.getFilterOptions();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch filter options');
    }
  }
);

export const fetchNearbyUsers = createAsyncThunk(
  'explore/fetchNearbyUsers',
  async (params: { 
    coordinates: { latitude: number; longitude: number }; 
    radius?: number; 
    filters?: Partial<ExploreFilters> 
  }, { rejectWithValue }) => {
    try {
      const response = await locationAPI.getNearbyUsers({
        latitude: params.coordinates.latitude,
        longitude: params.coordinates.longitude,
        radius: params.radius || 50,
        user_type: params.filters?.user_type,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch nearby users');
    }
  }
);

// export const saveFilterPreset = createAsyncThunk(
//   'explore/saveFilterPreset',
//   async (data: { name: string; filters: ExploreFilters }, { rejectWithValue }) => {
//     try {
//       const response = await exploreAPI.saveFilterPreset(data);
//       return response.data;
//     } catch (error: any) {
//       return rejectWithValue(error.response?.data?.error || 'Failed to save preset');
//     }
//   }
// );

// export const fetchFilterPresets = createAsyncThunk(
//   'explore/fetchFilterPresets',
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await exploreAPI.getFilterPresets();
//       return response.data;
//     } catch (error: any) {
//       return rejectWithValue(error.response?.data?.error || 'Failed to fetch presets');
//     }
//   }
// );

// export const deleteFilterPreset = createAsyncThunk(
//   'explore/deleteFilterPreset',
//   async (presetId: string, { rejectWithValue }) => {
//     try {
//       await exploreAPI.deleteFilterPreset(presetId);
//       return presetId;
//     } catch (error: any) {
//       return rejectWithValue(error.response?.data?.error || 'Failed to delete preset');
//     }
//   }
// );

// export const fetchFilterHistory = createAsyncThunk(
//   'explore/fetchFilterHistory',
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await exploreAPI.getFilterHistory();
//       return response.data;
//     } catch (error: any) {
//       return rejectWithValue(error.response?.data?.error || 'Failed to fetch history');
//     }
//   }
// );

const exploreSlice = createSlice({
  name: 'explore',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ExploreFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Don't clear users here to prevent UI flash. 
      // The users will be replaced when fetchExploreUsers fulfills.
    },
    replaceFilters: (state, action: PayloadAction<ExploreFilters>) => {
      state.filters = action.payload;
      state.users = [];
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.users = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    
  },
  extraReducers: (builder) => {
    builder
      // Fetch explore users
      .addCase(fetchExploreUsers.pending, (state, action) => {
        if (!action.meta.arg.loadMore) {
          state.loading = true;
        }
      })
      .addCase(fetchExploreUsers.fulfilled, (state, action) => {
        state.loading = false;
        const { data, loadMore } = action.payload;
        data.data = data.data == null ? [] : data.data        
        if (loadMore) {
          state.users = [...state.users, ...data.data];
        } else {
          state.users = data.data;
        }
        
        // Ensure pagination is always set with fallback values
        state.pagination = {
          current_page: data.pagination?.current_page || 1,
          per_page: data.pagination?.per_page || 20,
          total: data.pagination?.total || 0,
          total_pages: data.pagination?.total_pages || 0,
        };
        
        state.hasMore = (data.pagination?.current_page || 1) < (data.pagination?.total_pages || 0);
        state.filters = { ...state.filters, ...data.filters };
      })
      .addCase(fetchExploreUsers.rejected, (state, action) => {
        // console.log(action.payload)
        state.loading = false;
        state.error = action.payload as string;
        // toast.error('Failed to load users');
      })
      // .addCase(fetchExploreUsers.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = action.payload as string;
      //   toast.error('Failed to load users');
      // })
      
      // Fetch filter options
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })
      // Filter presets
      .addCase(saveFilterPreset.fulfilled, (state, action) => {
        state.filterPresets.push(action.payload);
        toast.success('Filter preset saved!');
      })
      .addCase(saveFilterPreset.rejected, (state, action) => {
        toast.error(action.payload as string);
      })
      
      .addCase(fetchFilterPresets.fulfilled, (state, action) => {
        state.filterPresets = action.payload;
      })
      
      .addCase(deleteFilterPreset.fulfilled, (state, action) => {
        state.filterPresets = state.filterPresets.filter(p => p.id !== action.payload);
        toast.success('Filter preset deleted');
      })
      .addCase(deleteFilterPreset.rejected, (state, action) => {
        toast.error(action.payload as string);
      })
      .addCase(fetchNearbyUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data;
        state.pagination = {
          current_page: 1,
          per_page: action.payload.data.length,
          total: action.payload.total,
          total_pages: 1,
        };
        state.hasMore = false; // No pagination for nearby search
      })
      // Filter history
      .addCase(fetchFilterHistory.fulfilled, (state, action) => {
        state.filterHistory = action.payload;
      });


  },
});

export const { setFilters, replaceFilters, clearFilters, clearError } = exploreSlice.actions;
export default exploreSlice.reducer;
